import { createApiClient } from '@reos/api-client';
import { problemCodeOf } from './api-error.js';
import type { SessionFlags, SessionUser, TenantInfo } from '../stores/session.js';

const baseUrl = (import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * Adresa API pro moduly, ktere nejdou pres `apiClient` (hlaseni chyb
 * z prohlizece, krok CER1). Drive si kazdy takovy modul cetl `VITE_API_URL`
 * sam — tahle konstanta je zacatek jednoho zdroje pravdy.
 */
export const API_BASE_URL = baseUrl;

// `fetch` predavame jako obalku, ne primo `globalThis.fetch`: openapi-fetch si
// referenci uklada pri vytvoreni klienta, takze test, ktery `fetch` nahradi az
// po importu modulu, by jinak mluvil se skutecnou siti.
export const apiClient = createApiClient({
  baseUrl,
  fetch: (...args) => globalThis.fetch(...args),
});

/**
 * Kolikrat se binarni odpoved zkusi stahnout, kdyz se PRETRHNE SPOJENI,
 * a jak dlouho se mezi pokusy ceka (pauza roste s poradim pokusu).
 */
const PDF_POKUSU = 3;
const PDF_PAUZA_MS = 1_000;

/** Odpoved serveru se stavem — tahle chyba se NEOPAKUJE (viz `fetchPdf`). */
class StavServeru extends Error {
  constructor(readonly stav: number) {
    super(`HTTP ${String(stav)}`);
    this.name = 'StavServeru';
  }
}

/**
 * PDF, KTERE SAZI SERVER (smlouva, doklad) — jedno misto pro oba.
 *
 * Binarni odpoved jde mimo `apiClient` (ten cte vzdy JSON). Do kroku QAFIX7
 * zily `downloadContractPdf` a `downloadInvoicePdf` jako dve shodne kopie
 * tehoz tela; ted obe volaji tuhle funkci.
 *
 * POKUS SE OPAKUJE, KDYZ SE PRETRHNE SPOJENI. Mereni P6/desktop 21. 10. 2026
 * 7:40:58: na stroji v tu sekundu zanikl kontejner a s nim veth na `docker0`
 * (`vethd514a93: Lost carrier`), Chrome z toho udelal `net::ERR_NETWORK_CHANGED`
 * a jediny `fetch` na `print.pdf` skoncil „Failed to fetch" — nginx zapsal
 * 499, tedy server se k odpovedi vubec nedostal. Makleřka v te chvili PDF
 * nedostane, prestoze ho server ochotne vysadi; jeden dalsi pokus to resi.
 *
 * ODPOVED SERVERU (4xx/5xx) se NEOPAKUJE: to je platna odpoved, ne pretrzene
 * spojeni, a opakovani by jen zatizilo Gotenberg.
 */
export async function downloadServerPdf(
  url: string,
  filename: string,
  /**
   * Krok VYV1: podklad, ktery server dostane v TELE pozadavku (papiry nabidky
   * — radky z karty, QR). Nezadane = GET jako doted (smlouva, doklad).
   */
  body?: unknown,
): Promise<void> {
  const blob = await fetchPdf(url, body);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

async function fetchPdf(url: string, body?: unknown): Promise<Blob> {
  let pokus = 1;
  const init: RequestInit =
    body === undefined
      ? { credentials: 'include' }
      : {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        };
  for (;;) {
    try {
      const res = await fetch(url, init);
      if (!res.ok) throw new StavServeru(res.status);
      // Cteni tela je ve TEMZE pokusu: spojeni se muze pretrhnout i uprostred
      // prenosu, a to je tataz vada jako pad pri navazovani.
      return await res.blob();
    } catch (err) {
      if (err instanceof StavServeru) throw new Error(err.message);
      if (pokus >= PDF_POKUSU) throw err;
      await new Promise((resolve) => setTimeout(resolve, PDF_PAUZA_MS * pokus));
      pokus += 1;
    }
  }
}

export interface LoginInput {
  readonly email: string;
  readonly password: string;
}

export async function signIn(input: LoginInput): Promise<void> {
  const res = await fetch(`${baseUrl}/api/auth/sign-in/email`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string; detail?: string } | null;
    throw new Error(body?.message ?? body?.detail ?? 'Přihlášení selhalo');
  }
}

/**
 * Nastavi aktivni organizaci na session (Better Auth). Bez ni ma session
 * `active_organization_id = null` a KAZDE domenove volani konci 403
 * „Uzivatel neni clenem zadne organizace" — `GET /api/me` pritom tenanta
 * odvodi z clenstvi, takze shell vypada prihlaseny a data presto nechodi
 * (nalez z e2e prubehu, krok S3). Volani je idempotentni.
 */
export async function setActiveOrganization(organizationId: string): Promise<void> {
  await fetch(`${baseUrl}/api/auth/organization/set-active`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ organizationId }),
  });
}

export async function signOut(): Promise<void> {
  await fetch(`${baseUrl}/api/auth/sign-out`, {
    method: 'POST',
    credentials: 'include',
  });
}

/** Chyba z Better Auth /api/auth/* - `code` je strojovy (napr. INVALID_TOKEN). */
export class AuthApiError extends Error {
  readonly code: string | undefined;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'AuthApiError';
    this.code = code;
  }
}

/**
 * Krok E2EFIX3 (nalez e2e S18i): na `/api/auth/*` nesedi jen Better Auth.
 * REOS pred jeho handler staví vlastni kontroly (`fastify-identity.ts`) a ty
 * odpovidaji **problem+json** — tedy `type`/`title`, ne `message`/`code`.
 * Puvodni cteni jen `message`/`code` z nich udelalo prazdno, takze obrazovka
 * sahla po obecne zalozni hlasce a uzivatel se nedozvedel, CO se stalo:
 * zmenu hesla v DEMO kancelari zakazuje DEMO11 (403 `demo/blocked`, title
 * „V ukázce se nic neodesílá…"), ale na obrazovce stalo „Změna hesla se
 * nezdařila. Zkuste to prosím znovu."
 *
 * `title` je uz lokalizovany text ze serveru (tyz princip jako
 * `DomainApiError.message` v `api-error.ts`) — smi se zobrazit primo.
 * `detail` se ZAMERNE nebere: u zakazu v ukazce je to technicka stopa
 * („demo: blocked operation 'account-change'").
 */
async function authErrorFrom(res: Response, fallback: string): Promise<AuthApiError> {
  const body = (await res.json().catch(() => null)) as
    | { message?: string; code?: string; title?: string; type?: string }
    | null;
  const code = body?.code ?? problemCodeOf(body?.type);
  return new AuthApiError(body?.message ?? body?.title ?? fallback, code);
}

/**
 * Zada o reset hesla. Odpoved API je VZDY stejna bez ohledu na existenci
 * e-mailu (better-auth to resi samo) - proto tato funkce nikdy nevyhazuje,
 * volajici obrazovka ukazuje jednotnou hlasku za kazdych okolnosti.
 */
export async function requestPasswordReset(email: string, redirectTo: string): Promise<void> {
  try {
    await fetch(`${baseUrl}/api/auth/request-password-reset`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, redirectTo }),
    });
  } catch {
    // sitova chyba se take neprojevi navenek - jednotna odpoved v UI
  }
}

export interface ConfirmPasswordResetInput {
  readonly token: string;
  readonly newPassword: string;
}

export async function confirmPasswordReset(input: ConfirmPasswordResetInput): Promise<void> {
  const res = await fetch(`${baseUrl}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await authErrorFrom(res, 'Nastavení nového hesla selhalo');
}

export interface ChangePasswordInput {
  readonly currentPassword: string;
  readonly newPassword: string;
  readonly revokeOtherSessions: boolean;
}

/**
 * Krok UXP8 — `POST /api/auth/change-password` (Better Auth), vyzaduje
 * aktivni session. Spatne soucasne heslo prichazi jako `code: 'INVALID_PASSWORD'`,
 * uzivatel bez hesla (jen passkey/magic link) jako `code: 'CREDENTIAL_ACCOUNT_NOT_FOUND'`.
 */
export async function changePassword(input: ChangePasswordInput): Promise<void> {
  const res = await fetch(`${baseUrl}/api/auth/change-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await authErrorFrom(res, 'Změna hesla se nezdařila');
}

/**
 * Krok UXP8 bod 4 — zjisti, jestli ma prihlaseny uzivatel ucet s heslem
 * (`providerId: 'credential'`). Pri chybe site vraci `true` (bezpecny vychozi
 * stav je ukazat formular, ne ho schovat).
 */
export async function hasPasswordAccount(): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl}/api/auth/list-accounts`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    if (!res.ok) return true;
    const accounts = (await res.json()) as { providerId: string }[];
    return accounts.some((a) => a.providerId === 'credential');
  } catch {
    return true;
  }
}

export interface SignUpInput {
  readonly email: string;
  readonly password: string;
  readonly name: string;
}

export async function signUp(input: SignUpInput): Promise<void> {
  const res = await fetch(`${baseUrl}/api/auth/sign-up/email`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await authErrorFrom(res, 'Založení účtu selhalo');
}

export interface InvitationInfo {
  readonly id: string;
  readonly email: string;
  readonly role: string;
  readonly organizationName: string;
  readonly status: string;
}

/** null = pozvanku nelze nacist (neni prihlasen / neni prijemce / expirovala). */
export async function getInvitation(id: string): Promise<InvitationInfo | null> {
  const res = await fetch(`${baseUrl}/api/auth/organization/get-invitation?id=${encodeURIComponent(id)}`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as {
    id: string;
    email: string;
    role: string;
    organizationName: string;
    status: string;
  };
  return data;
}

export async function acceptInvitation(invitationId: string): Promise<void> {
  const res = await fetch(`${baseUrl}/api/auth/organization/accept-invitation`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ invitationId }),
  });
  if (!res.ok) throw await authErrorFrom(res, 'Přijetí pozvánky selhalo');
}

/**
 * Krok TRIAL1 — stav zkusebniho obdobi kancelare, jak ho hlasi `GET /api/me`.
 * `null` = kancelar zkusebni obdobi nema (nebo starsi server pole neposila);
 * pruh se pak nekresli.
 */
export interface TrialStatus {
  readonly endsAt: string | null;
  /** Celych dni do konce; `0` = konci dnes, zaporne = uz skoncilo. */
  readonly daysLeft: number | null;
  readonly expired: boolean;
  readonly paid: boolean;
  /** Po konci bez platby je aplikace jen ke cteni (zapisove API vraci 402). */
  readonly readOnly: boolean;
  /** Kam zaplatit predplatne (tok `/tarif` v aplikaci); `null` = odkaz neukazovat. */
  readonly payUrl: string | null;
  /**
   * Krok PRO1: prah upozorneni, ktery je prave splneny (7 nebo 1 den);
   * `null` = dnes nic nedozralo. Volitelne: starsi nasazeni API pole
   * neposila a pruh se pak chova jako predtim.
   */
  readonly noticeThreshold?: number | null;
  /**
   * Krok PRO1: je platebni cesta otevrena (`commerce.legal_approved`)?
   * `false` = odkaz na platbu se NEUKAZE, misto nej jde srozumitelna hlaska.
   * Chybejici pole (starsi API) znamena „neomezovat" — pojistka je stejne na
   * serveru (`createOfficePlanCheckout`).
   */
  readonly checkoutOpen?: boolean;
}

export interface MeResponse {
  readonly user: SessionUser;
  readonly tenant: TenantInfo | null;
  readonly flags: SessionFlags;
  /** Krok TRIAL1: zkusebni obdobi kancelare (pruh v hlavicce, karta v Nastaveni). */
  readonly trial: TrialStatus | null;
  /**
   * Krok UXV4p bod 3 — „Velikost písma" a „Vzhled" uložené U UŽIVATELE.
   * Jdou TOUTO odpovědí, ne vlastním dotazem: admin na `/api/me` při startu
   * čeká tak jako tak, takže se obrazovka po jejich načtení nemusí
   * překreslovat („bez bliknutí", zadání bod 3).
   */
  readonly ui: UiPreferences;
}

/** Hodnoty jsou tytéž, jaké nese `data-font-size` / volba vzhledu na `<html>`. */
export interface UiPreferences {
  readonly fontSize: 'small' | 'normal' | 'large' | 'largest';
  readonly theme: 'light' | 'dark' | 'system';
}

export async function fetchMe(): Promise<MeResponse | null> {
  const res = await fetch(`${baseUrl}/api/me`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (res.status === 401) return null;
  if (!res.ok) {
    throw new Error('Načtení session selhalo');
  }
  const data = (await res.json()) as {
    user: {
      id: string;
      email: string;
      name: string;
      roles: string[];
      tenantId: string | null;
      locale: string;
      portrait?: string | null;
      uiFontSize?: UiPreferences['fontSize'];
      uiTheme?: UiPreferences['theme'];
    };
    tenant: { id: string; name: string; offices: { id: string; name: string }[] } | null;
    flags?: {
      pilotMenu: boolean;
      showHiddenSections: boolean;
      supportReporting?: boolean;
      demo?: boolean;
      publicDemo?: boolean;
      knOutputs?: boolean;
    };
    trial?: TrialStatus | null;
  };
  const user: SessionUser = {
    id: data.user.id,
    email: data.user.email,
    name: data.user.name,
    roles: data.user.roles,
    tenantId: data.user.tenantId ?? '',
    // Krok NAP3 bod 2: starsi server pole neposila — profil pak nabidne
    // nahrani portretu a zadny stavajici neukazuje (nic si nevymysli).
    portrait: data.user.portrait ?? null,
  };
  const tenant: TenantInfo | null = data.tenant
    ? { id: data.tenant.id, name: data.tenant.name, offices: data.tenant.offices }
    : null;
  return {
    user,
    tenant,
    flags: {
      pilotMenu: data.flags?.pilotMenu ?? false,
      showHiddenSections: data.flags?.showHiddenSections ?? false,
      // Krok MOB1: starší server pole neposílá — hlášení se pak NENABÍZÍ
      // (raději skryté tlačítko než tlačítko, které skončí chybou).
      supportReporting: data.flags?.supportReporting ?? false,
      // Krok D1 bod 3: starší server pole neposílá — pruh „Ukázková data"
      // se pak NEUKAZUJE (raději bez pruhu než pruh v živé kanceláři).
      demo: data.flags?.demo ?? false,
      // Krok DEMO7: starsi server pole neposila — pruh pak NESLIBUJE hodinovy
      // reset a prevereni zustava „placeny beh" (rader pravda o ostre
      // instanci nez slib, ktery nikdo neplni).
      publicDemo: data.flags?.publicDemo ?? false,
      // Krok KN4 bod 2: starsi server pole neposila — udaje z katastru se pak
      // NEZOBRAZUJI (fail-closed: § 53 katastralniho zakona je zakaz sireni,
      // takze „nevime" musi znamenat „neukazuj", ne „ukaz").
      knOutputs: data.flags?.knOutputs ?? false,
    },
    // Krok TRIAL1: starsi server pole neposila — pruh ani karta pak netvrdi
    // nic o zkusebnim obdobi (raději bez pruhu než pruh s vymyslenym datem).
    trial: data.trial ?? null,
    // Starší server (nasazení před krokem UXV4p) pole neposílá — ponecháme
    // výchozí, aby admin fungoval i proti němu (expand/contract). Krok WEBN3
    // (zadání bod 1): výchozí vzhled je VŽDY SVĚTLÝ, ne „podle systému".
    ui: { fontSize: data.user.uiFontSize ?? 'normal', theme: data.user.uiTheme ?? 'light' },
  };
}

/**
 * Krok UXV4p bod 3 — uloží volbu k uživateli, aby platila na všech jeho
 * zařízeních. Server zapisuje vždy jen přihlášeného uživatele.
 */
export async function saveUiPreferences(patch: Partial<UiPreferences>): Promise<UiPreferences | null> {
  /*
   * `null` = neulozilo se na server. Volba uz JE v prohlížeči a na obrazovce
   * vidět, takže selhání serveru (offline, starší nasazení bez endpointu)
   * NESMÍ shodit obrazovku ani vyhodit neodchycené odmítnutí slibu — jen se
   * nepropíše na ostatní zařízení. Proto se tu chytá i chyba sítě samotná.
   */
  try {
    const res = await fetch(`${baseUrl}/api/me/ui-preferences`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        ...(patch.fontSize !== undefined ? { uiFontSize: patch.fontSize } : {}),
        ...(patch.theme !== undefined ? { uiTheme: patch.theme } : {}),
      }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { uiFontSize: UiPreferences['fontSize']; uiTheme: UiPreferences['theme'] };
    return { fontSize: data.uiFontSize, theme: data.uiTheme };
  } catch {
    return null;
  }
}

/**
 * Krok PIL1 (vada V2) — uloží jméno přihlášeného člověka. Na rozdíl od
 * `saveUiPreferences` se chyba NEPOLYKÁ: je to vědomé odeslání formuláře,
 * a kdyby se tiše nepovedlo, makléřka by se na totéž jméno ptala každý den.
 */
export async function saveMyName(name: string): Promise<string> {
  const res = await fetch(`${baseUrl}/api/me/profile`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`PATCH /api/me/profile: ${String(res.status)}`);
  const data = (await res.json()) as { name: string };
  return data.name;
}

/**
 * Krok NAP3 bod 2 (A27) — uloží nebo smaže PORTRÉT přihlášeného makléře.
 * `null` = smazat. Chyba se NEPOLYKÁ (stejně jako u jména): je to vědomé
 * odeslání a makléř musí vědět, že portrét v reportu nebude.
 */
export async function saveMyPortrait(portrait: string | null): Promise<string | null> {
  const res = await fetch(`${baseUrl}/api/me/portrait`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ portrait }),
  });
  if (!res.ok) throw new Error(`PATCH /api/me/portrait: ${String(res.status)}`);
  const data = (await res.json()) as { portrait: string | null };
  return data.portrait;
}

// backward compat alias
export const login = signIn;

export { baseUrl };
